import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';
import { setCredentials, logout as logoutAction, setError, setLoading } from '../slices/authSlice';
import { TOKEN_EXPIRY_KEY } from '@/constants/auth';

/**
 * Clear any stale client-side auth artefacts before starting a fresh
 * login. See #397 — a leftover accessToken in localStorage races the
 * next login attempt: any authed request fired between the login POST
 * and setCredentials picks up the stale bearer, gets a 401, triggers
 * the response-interceptor refresh, which 401s against a stale cookie,
 * which dispatches logout() and nukes the session we just created.
 *
 * Synchronous clears (localStorage + Redux) run before the request
 * interceptor reads either one, so nothing downstream can grab the
 * old token. We deliberately DON'T call /auth/logout server-side:
 * the fresh login response overwrites the refresh cookie, and doing
 * a logout first would add a round-trip that most users don't need.
 */
const clearStaleAuthState = (dispatch: (action: any) => void): void => {
  if (localStorage.getItem('accessToken') || localStorage.getItem(TOKEN_EXPIRY_KEY)) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    dispatch(logoutAction());
  }
};

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
}

interface AuthResponse {
  user: {
    id: string;
    email?: string;
    username?: string;
    walletAddress?: string;
    email_verified?: boolean;
    isFirstLogin?: boolean;
    hasProfile?: boolean;
    hasBlockchainProfile?: boolean;
    authMethod?: 'email' | 'wallet';
    role?: 'user' | 'admin' | 'moderator';
  };
  accessToken: string;
}

interface WalletLoginCredentials {
  walletAddress: string;
  signature: string;
  message: string;
}

// Login thunk
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { dispatch, rejectWithValue }) => {
    try {
      clearStaleAuthState(dispatch);
      dispatch(setLoading(true));
      const response = await api.post<AuthResponse>('/auth/login', credentials);
      dispatch(setCredentials(response.data));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Wallet login thunk
export const walletLogin = createAsyncThunk(
  'auth/walletLogin',
  async (credentials: WalletLoginCredentials, { dispatch, rejectWithValue }) => {
    try {
      clearStaleAuthState(dispatch);
      dispatch(setLoading(true));
      const response = await api.post<AuthResponse>('/auth/wallet-login', credentials);
      dispatch(setCredentials(response.data));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Wallet login failed';
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Register thunk
export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post<AuthResponse>('/auth/register', data);
      // Don't set credentials on registration - user must verify email first
      // dispatch(setCredentials(response.data));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Logout thunk
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    try {
      // Call logout endpoint to clear refresh token cookie
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state
      dispatch(logoutAction());
    }
  }
);

// Fetch current user thunk (for app initialization)
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get<{ user: any }>('/auth/me');
      // IMPORTANT: Update user in Redux state
      const userData = response.data.user;
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        dispatch(setCredentials({ user: userData, accessToken }));
      }
      return userData;
    } catch (error: any) {
      // Token is invalid, logout
      dispatch(logoutAction());
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user');
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Refresh access token thunk
export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Canonical route — server also has `/auth/refresh` as a legacy
      // alias (auth re-audit 2026-07-04 N-13).
      const response = await api.post<{ accessToken: string }>('/auth/refresh-token');
      return response.data.accessToken;
    } catch (error: any) {
      dispatch(logoutAction());
      return rejectWithValue(error.response?.data?.message || 'Token refresh failed');
    }
  }
);
